import React from 'react';
import { Chip, ChipProps, IconButton } from '@mui/joy';
import { Close } from '@mui/icons-material';
import { Tag } from '../types/tag';

interface TagChipProps extends Omit<ChipProps, 'color'> {
  tag: Tag;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'outlined' | 'soft';
  onClick?: () => void;
  onDelete?: () => void;
  deletable?: boolean;
}

export const TagChip: React.FC<TagChipProps> = ({
  tag,
  size = 'sm',
  variant = 'soft',
  onClick,
  onDelete,
  deletable = false,
  ...props
}) => {
  const chipProps: ChipProps = {
    size,
    variant,
    onClick: deletable && onDelete ? undefined : onClick, // Disable chip onClick if deletable
    sx: {
      backgroundColor: `${tag.color}20`, // 20% opacity
      color: tag.color,
      borderColor: tag.color,
      position: 'relative',
      '&:hover': {
        backgroundColor: `${tag.color}30`, // 30% opacity on hover
      },
      ...props.sx,
    },
    ...props,
  };

  // Add delete icon as endDecorator if deletable
  if (deletable && onDelete) {
    chipProps.endDecorator = (
      <IconButton
        className="tag-delete-button"
        size="sm"
        variant="plain"
        color="neutral"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onDelete();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseUp={(e) => {
          e.stopPropagation();
        }}
        sx={{
          minHeight: 'auto',
          minWidth: 'auto',
          width: '18px',
          height: '18px',
          padding: 0,
          marginLeft: 0.5,
          position: 'relative',
          zIndex: 100,
          cursor: 'pointer',
          pointerEvents: 'auto',
          '&:hover': {
            backgroundColor: `${tag.color}40`,
            color: tag.color,
          },
          '&:active': {
            backgroundColor: `${tag.color}50`,
          },
        }}
      >
        <Close sx={{ fontSize: '14px', pointerEvents: 'none' }} />
      </IconButton>
    );
  }

  return (
    <Chip {...chipProps}>
      {tag.icon && <span className="tag-icon">{tag.icon}</span>}
      {tag.name}
    </Chip>
  );
}; 